const WIDGET_SRC = "https://dashboard.retellai.com/retell-widget.js";
const WIDGET_SCRIPT_ID = "retell-widget";

// Brand values mirror the voice demo page (App.tsx): primary #214eff, headline
// "Meet Havana", logo at /havana_logo.svg.
const WIDGET_TITLE = "Meet Havana";
const WIDGET_BOT_NAME = "Havana";
const WIDGET_COLOR = "#214eff";
const WIDGET_LOGO_PATH = "/havana_logo.svg";

// Retell renders the widget into an open shadow root attached to a host <div>
// in document.body, with a `.retell-powered-by` node inside. External CSS
// doesn't pierce shadow boundaries, so we (a) inject a <style> directly into
// each open shadow root we discover, and (b) walk shadow roots from a
// MutationObserver to catch hosts added after page load.
const BRANDING_CSS = `
  .retell-powered-by,
  [class*="powered-by" i],
  [class*="powered_by" i],
  a[href*="retellai.com"] {
    display: none !important;
    visibility: hidden !important;
    height: 0 !important;
    overflow: hidden !important;
  }
`;

const STYLE_MARKER = "data-retell-branding-hide";

function injectStyleIntoShadow(shadow: ShadowRoot) {
  if (shadow.querySelector(`style[${STYLE_MARKER}]`)) return;
  const style = document.createElement("style");
  style.setAttribute(STYLE_MARKER, "");
  style.textContent = BRANDING_CSS;
  shadow.appendChild(style);
}

function processNode(node: Node) {
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as Element;
  if (el.shadowRoot) injectStyleIntoShadow(el.shadowRoot);
  // Recurse into descendants in case a host is nested
  el.querySelectorAll("*").forEach((child) => {
    if ((child as Element).shadowRoot) {
      injectStyleIntoShadow((child as Element).shadowRoot!);
    }
  });
}

function startBrandingObserver() {
  // Sweep existing nodes
  document.querySelectorAll("*").forEach((el) => {
    if ((el as Element).shadowRoot) {
      injectStyleIntoShadow((el as Element).shadowRoot!);
    }
  });
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach(processNode);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

// The Retell widget persists the conversation in localStorage and rehydrates
// it on load. There is no config attribute to disable that, so we clear its
// keys before injecting the script to start every page load with a fresh chat.
const RETELL_CHAT_STORAGE_KEYS = ["LS_CHAT_ID", "LS_CHAT_HISTORY", "LS_CHAT_LIST"];

function clearPersistedChat() {
  try {
    RETELL_CHAT_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    // localStorage can throw in private-browsing / storage-blocked contexts;
    // the widget just falls back to an empty chat.
  }
}

function isTruthy(v: string | undefined): boolean {
  if (!v) return false;
  return ["1", "true", "yes", "on"].includes(v.toLowerCase());
}

export function injectRetellChatWidget() {
  if (!isTruthy(process.env.REACT_APP_RETELL_CHAT_WIDGET_ENABLED)) return;
  if (document.getElementById(WIDGET_SCRIPT_ID)) return;

  const params = new URLSearchParams(window.location.search);
  const chatAgentIdFromUrl = (params.get("chat_agent_id") || "").trim();
  const voiceAgentIdFromUrl = (params.get("agent_id") || "").trim();

  // When the URL specifies a real voice ?agent_id=<value>, the chat widget is
  // opt-in via ?chat_agent_id=. An empty ?agent_id= (or no agent_id) is
  // treated as "no override" and falls through to the env default.
  if (voiceAgentIdFromUrl && !chatAgentIdFromUrl) return;

  const publicKey = process.env.REACT_APP_RETELL_CHAT_PUBLIC_KEY;
  const agentId =
    chatAgentIdFromUrl || process.env.REACT_APP_RETELL_CHAT_AGENT_ID;
  if (!publicKey || !agentId) {
    console.warn(
      "[retell-chat-widget] enabled but REACT_APP_RETELL_CHAT_PUBLIC_KEY or chat agent id is missing",
    );
    return;
  }

  clearPersistedChat();

  const script = document.createElement("script");
  script.id = WIDGET_SCRIPT_ID;
  script.src = WIDGET_SRC;
  script.type = "module";
  script.setAttribute("data-public-key", publicKey);
  script.setAttribute("data-agent-id", agentId);
  script.setAttribute("data-title", WIDGET_TITLE);
  script.setAttribute("data-bot-name", WIDGET_BOT_NAME);
  script.setAttribute("data-color", WIDGET_COLOR);
  script.setAttribute("data-logo-url", window.location.origin + WIDGET_LOGO_PATH);

  document.head.appendChild(script);

  if (document.body) {
    startBrandingObserver();
  } else {
    window.addEventListener("DOMContentLoaded", startBrandingObserver, {
      once: true,
    });
  }
}
